import { z } from "zod";

// Base schemas
export const PackageNameSchema = z
  .string()
  .min(1, "Package name is required")
  .max(50, "Package name must be less than 50 characters")
  .regex(
    /^[a-z0-9-_/]+$/,
    "Package name can only contain lowercase letters, numbers, hyphens, underscores, and slashes",
  );

export const ScopeSchema = z
  .string()
  .regex(
    /^@[a-z0-9-_]+$/,
    "Scope must start with @ and contain only lowercase letters, numbers, hyphens, and underscores",
  )
  .optional();

export const VERSION_REGEX =
  /^\d+\.\d+\.\d+(-[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*)?(\+[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*)?$/;

export const SemanticVersionSchema = z
  .string()
  .regex(VERSION_REGEX, "Invalid semantic version format");

export const PackageVisibilitySchema = z.enum([
  "public",
  "private",
  "org",
  "user",
]);
export const PackageAccessSchema = z.enum(["public", "private"]);

// File upload schemas
export const UploadedFileSchema = z.object({
  filename: z.string(),
  contentType: z.string(),
  content: z.instanceof(Uint8Array),
  size: z.number().positive(),
});

// Manifest schema (codemod.yaml)
export const ManifestSchema = z.object({
  schema_version: z.literal("1.0"),
  name: PackageNameSchema,
  version: SemanticVersionSchema,
  description: z
    .string()
    .min(1, "Description is required")
    .max(500, "Description must be less than 500 characters"),
  author: z
    .string()
    .min(1, "Author is required")
    .max(255, "Author must be less than 255 characters"),
  license: z
    .string()
    .max(100, "License must be less than 100 characters")
    .optional(),
  workflow: z.string().min(1, "Workflow file path is required"),

  // Registry settings
  registry: z
    .object({
      access: PackageAccessSchema.default("public"),
      visibility: PackageVisibilitySchema.default("public"),
      scope: ScopeSchema,
    })
    .optional(),

  // Target specifications
  targets: z
    .object({
      languages: z.array(z.string()).default([]),
      frameworks: z.array(z.string()).default([]),
      versions: z.record(z.string()).default({}),
    })
    .optional(),

  // Dependencies and metadata
  dependencies: z.array(z.string()).default([]),
  keywords: z
    .array(z.string())
    .max(20, "Maximum 20 keywords allowed")
    .default([]),
  category: z
    .enum(["migration", "analysis", "formatting", "optimization"])
    .optional(),
  repository: z.string().url("Invalid repository URL").optional(),
  homepage: z.string().url("Invalid homepage URL").optional(),
  documentation: z.string().url("Invalid documentation URL").optional(),
  bugs: z.string().url("Invalid bugs URL").optional(),
  readme: z.string().optional(),
  changelog: z.string().optional(),
});

// API request/response schemas
export const PublishPackageRequestSchema = z.object({
  manifest: ManifestSchema,
  checksum: z.string().length(64, "Invalid checksum - must be 64 characters"),
  size: z
    .number()
    .positive("Size must be positive")
    .max(50 * 1024 * 1024, "Package size cannot exceed 50MB"),
});

// Multipart form upload schema
export const PublishPackageFormSchema = z.object({
  packageFile: UploadedFileSchema.refine(
    (file) =>
      file.contentType === "application/gzip" ||
      file.contentType === "application/x-gzip",
    "Package must be a gzip file",
  )
    .refine(
      (file) => file.filename.endsWith(".tar.gz"),
      "Package must be a .tar.gz file",
    )
    .refine(
      (file) => file.size <= 10 * 1024 * 1024, // 10MB compressed
      "Compressed package size cannot exceed 10MB",
    ),
  manifest: z.string().transform((str, ctx) => {
    try {
      const parsed = JSON.parse(str) as unknown;
      return ManifestSchema.parse(parsed);
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid manifest JSON",
      });
      return z.NEVER;
    }
  }),
});

export const GetPackageParamsSchema = z.object({
  scope: z.string().optional(),
  name: PackageNameSchema,
});

export const GetPackageVersionParamsSchema = z.object({
  scope: z.string().optional(),
  name: PackageNameSchema,
  version: SemanticVersionSchema,
});

export const SearchPackagesQuerySchema = z.object({
  q: z
    .string()
    .min(1, "Search query is required")
    .max(100, "Search query too long")
    .optional(),
  language: z.string().max(50, "Language filter too long").optional(),
  framework: z.string().max(50, "Framework filter too long").optional(),
  category: z
    .enum(["migration", "analysis", "formatting", "optimization"])
    .optional(),
  page: z.coerce.number().positive().default(1),
  scope: z.string().optional(),
  limit: z.coerce.number().min(1).max(50).default(12),
  includeLegacy: z.boolean().optional().default(true),
});

// Response schemas
export const PackageVersionResponseSchema = z.object({
  id: z.string().uuid(),
  version: SemanticVersionSchema,
  description: z.string().nullable(),
  published_at: z.string().datetime(),
  size: z.number(),
  checksum: z.string(),
  download_count: z.number(),
  deprecated: z.boolean(),
  deprecation_message: z.string().nullable(),
  targets: z.object({
    languages: z.array(z.string()),
    frameworks: z.array(z.string()),
    versions: z.record(z.string()),
  }),
  dependencies: z.array(z.string()),
  published_by: z.object({
    id: z.string().uuid(),
    username: z.string(),
    name: z.string(),
  }),
});

export const PackageResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  scope: z.string().nullable(),
  display_name: z.string().nullable(),
  description: z.string().nullable(),
  author: z.string(),
  license: z.string().nullable(),
  repository: z.string().nullable(),
  homepage: z.string().nullable(),
  documentation: z.string().nullable(),
  bugs_url: z.string().nullable(),
  keywords: z.array(z.string()),
  category: z.string().nullable(),
  visibility: PackageVisibilitySchema,
  access: PackageAccessSchema,
  latest_version: z.string().nullable(),
  download_count: z.number(),
  is_legacy: z.boolean(),
  star_count: z.number(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  owner: z.object({
    id: z.string().uuid(),
    username: z.string(),
    name: z.string(),
  }),
  organization: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      slug: z.string(),
    })
    .nullable(),
  versions: z.record(PackageVersionResponseSchema).optional(),
});

export const PublishPackageResponseSchema = z.object({
  success: z.boolean(),
  package: z.object({
    id: z.string().uuid(),
    name: z.string(),
    version: z.string(),
    scope: z.string().nullable(),
    download_url: z.string().url(),
    published_at: z.string().datetime(),
  }),
});

export const SearchPackagesResponseSchema = z.object({
  total: z.number(),
  packages: z.array(PackageResponseSchema.omit({ versions: true })),
});

export const DownloadPackageResponseSchema = z.object({
  download_url: z.string().url(),
  expires_at: z.string().datetime(),
});

// Error response schema
export const ErrorResponseSchema = z.object({
  error: z.string(),
  message: z.string(),
  details: z
    .array(
      z.object({
        field: z.string(),
        issue: z.string(),
      }),
    )
    .optional(),
});

// Package validation schemas for upload
export const PackageBundleValidationSchema = z.object({
  has_manifest: z.boolean(),
  has_workflow: z.boolean(),
  manifest_valid: z.boolean(),
  workflow_valid: z.boolean(),
  size_valid: z.boolean(),
  structure_valid: z.boolean(),
  errors: z.array(z.string()),
  warnings: z.array(z.string()),
});

// S3 Upload Result Schema
export const S3UploadResultSchema = z.object({
  key: z.string(),
  bucket: z.string(),
  url: z.string().url(),
  etag: z.string(),
  size: z.number(),
});

// TypeScript types
export type PackageName = z.infer<typeof PackageNameSchema>;
export type Scope = z.infer<typeof ScopeSchema>;
export type SemanticVersion = z.infer<typeof SemanticVersionSchema>;
export type PackageVisibility = z.infer<typeof PackageVisibilitySchema>;
export type PackageAccess = z.infer<typeof PackageAccessSchema>;
export type UploadedFile = z.infer<typeof UploadedFileSchema>;
export type Manifest = z.infer<typeof ManifestSchema>;
export type PublishPackageRequest = z.infer<typeof PublishPackageRequestSchema>;
export type PublishPackageForm = z.infer<typeof PublishPackageFormSchema>;
export type GetPackageParams = z.infer<typeof GetPackageParamsSchema>;
export type GetPackageVersionParams = z.infer<
  typeof GetPackageVersionParamsSchema
>;
export type SearchPackagesQuery = z.infer<typeof SearchPackagesQuerySchema>;
export type PackageVersionResponse = z.infer<
  typeof PackageVersionResponseSchema
>;
export type PackageResponse = z.infer<typeof PackageResponseSchema>;
export type PublishPackageResponse = z.infer<
  typeof PublishPackageResponseSchema
>;
export type SearchPackagesResponse = z.infer<
  typeof SearchPackagesResponseSchema
>;
export type DownloadPackageResponse = z.infer<
  typeof DownloadPackageResponseSchema
>;
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;
export type PackageBundleValidation = z.infer<
  typeof PackageBundleValidationSchema
>;
export type S3UploadResult = z.infer<typeof S3UploadResultSchema>;
